import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, TriangleAlert as AlertTriangle, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import './NotificationBell.css';

interface Notification {
  id: number;
  message: string;
  is_read: boolean;
  created_at: string;
  title?: string;
}

interface NotificationsResponse {
  success: boolean;
  notifications: Notification[];
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

const POLL_INTERVAL = 60000;

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [markingId, setMarkingId] = useState<number | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const getToken = () => localStorage.getItem('authToken') || '';

  const fetchUnreadCount = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        // API may return a number directly, or { count: N }, or { unread_count: N }
        if (typeof data === 'number') {
          setUnreadCount(data);
        } else if (typeof data?.count === 'number') {
          setUnreadCount(data.count);
        } else if (typeof data?.unread_count === 'number') {
          setUnreadCount(data.unread_count);
        }
      }
    } catch {
      // Silently fail — badge is informational
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setListLoading(true);
    setListError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/notifications`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (res.ok) {
        const data: NotificationsResponse = await res.json();
        setNotifications(data.notifications || []);
      } else {
        setListError('Failed to load notifications.');
      }
    } catch {
      setListError('Network error. Please try again.');
    } finally {
      setListLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id: number) => {
    const token = getToken();
    if (!token || markingId !== null) return;
    setMarkingId(id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/notifications/${id}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (res.ok) {
        setNotifications(prev =>
          prev.map(n => n.id === id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {
      // Silently fail
    } finally {
      setMarkingId(null);
    }
  }, [markingId]);

  // Initial unread count + polling
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch list when panel opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="nb-root">
      <button
        ref={buttonRef}
        className={`nb-bell-btn${open ? ' nb-bell-btn--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="nb-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className="nb-panel">
          <div className="nb-panel-header">
            <span className="nb-panel-title">Notifications</span>
            <div className="nb-panel-actions">
              <button
                className="nb-icon-btn"
                onClick={fetchNotifications}
                disabled={listLoading}
                title="Refresh"
              >
                <RefreshCw size={14} className={listLoading ? 'nb-spinning' : ''} />
              </button>
              <button className="nb-icon-btn" onClick={() => setOpen(false)} title="Close">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="nb-panel-body">
            {listLoading && (
              <div className="nb-state nb-state--loading">
                <RefreshCw size={20} className="nb-spinning" />
                <span>Loading...</span>
              </div>
            )}

            {listError && !listLoading && (
              <div className="nb-state nb-state--error">
                <AlertTriangle size={16} />
                <span>{listError}</span>
              </div>
            )}

            {!listLoading && !listError && notifications.length === 0 && (
              <div className="nb-state nb-state--empty">
                <Bell size={32} />
                <span>You have no notifications</span>
              </div>
            )}

            {!listLoading && !listError && notifications.length > 0 && (
              <ul className="nb-list">
                {notifications.map(n => (
                  <li key={n.id} className={`nb-item${n.is_read ? ' nb-item--read' : ''}`}>
                    <div className="nb-item-body">
                      {n.title && <p className="nb-item-title">{n.title}</p>}
                      <p className="nb-item-message">{n.message}</p>
                      <span className="nb-item-time">{timeAgo(n.created_at)}</span>
                    </div>
                    {!n.is_read && (
                      <button
                        className="nb-mark-read-btn"
                        onClick={() => markAsRead(n.id)}
                        disabled={markingId === n.id}
                        title="Mark as read"
                      >
                        {markingId === n.id ? (
                          <RefreshCw size={12} className="nb-spinning" />
                        ) : (
                          <Check size={12} />
                        )}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
